import   axios  from 'axios';

import { BackendResponse ,
         handleError     }  from '../../dataTypes/backendServicesManagment/types';

import { BetaApplication          ,
         BetaApplicationEquipment ,
         BetaApplicationStatus    }  from '../../dataTypes/betaApplicationManagment/types';

import { API_URLS }  from '../urls';

import { getUserAgentInformations }  from '../../utils/utils_userAgent';
import { getAppInformations       }  from '../../utils/utils_app';


const buildHeaders = ( adminId?: number ) => ({
  'Accept'                  : 'application/ld+json',
  'User-Agent-Informations' : `${getUserAgentInformations ()}`,
  'App-Informations'        : `${getAppInformations ()}`,
  'X-Admin-Id'              : adminId ?? ''
});

const toDate = ( value: any ): Date | null => value ? new Date ( value ) : null;

const toBetaApplication = ( raw: any ): BetaApplication => {

  const equipments: Record<string, BetaApplicationEquipment> = {};

  Object.entries ( raw.equipments ?? {} ).forEach ( ( [ type , details ]: [ string , any ] ) => {
    equipments [ type ] = {
                            model            : details?.model ?? null ,
                            operatingSystems : Array.isArray ( details?.operatingSystems ) ? details.operatingSystems : [] ,
                          };
  });

  return {
           id                : raw.id ,
           firstname         : raw.firstname     ?? '' ,
           lastname          : raw.lastname      ?? '' ,
           email             : raw.email         ?? '' ,
           phoneNumber       : raw.phoneNumber   ?? '' ,
           ageRange          : raw.ageRange      ?? '' ,
           activity          : raw.activity      ?? '' ,
           activityOther     : raw.activityOther ?? '' ,
           organization      : raw.organization  ?? '' ,
           website           : raw.website       ?? '' ,
           publicName        : raw.publicName    ?? '' ,
           description       : raw.description   ?? '' ,
           equipments        : equipments ,
           cguAcceptedDate   : toDate ( raw.cguAcceptedDate ) ,
           emailValidated    : raw.emailValidated === true ,
           isPromotion       : raw.isPromotion === true ,
           existingUserId    : raw.existingUserId ?? null ,
           emailValidationDate : toDate ( raw.emailValidationDate ) ,
           status            : raw.status ,
           creationDate      : toDate ( raw.creationDate ) ,
           reviewedDate      : toDate ( raw.reviewedDate ) ,
           reviewedByAdminId : raw.reviewedByAdminId ?? null ,
           createdAdminId    : raw.createdAdminId    ?? null ,
           ipAddress         : raw.ipAddress ?? '' ,
         };
};


export interface BackendResponse_BetaApplications extends BackendResponse<BetaApplication[]> {}

// Les plus récentes d'abord ; filtre facultatif sur le statut
export const getBetaApplications = async ( status?: BetaApplicationStatus ): Promise<BackendResponse_BetaApplications> => {

  const url = `${API_URLS.getBetaApplications}?limit=500${status ? `&status=${status}` : ''}`;

  try
   {
    const response = await axios.get ( url , { headers: buildHeaders () } );

    if ( response.status === 200 )
          {
           return {
                    code    : 1 ,
                    comment : 'The retrieval of beta applications is complete.' ,
                    payload : ( response.data.items ?? [] ).map ( toBetaApplication ) ,
                  };
          }
     else {
           return handleError<BetaApplication[]> ( response.status , 'Non-200 response from API' );
          }
   }
  catch ( error )
   {
    if ( axios.isAxiosError ( error ) )
     {
      return handleError<BetaApplication[]> ( -101 , error.response?.data?.detail ?? error.message );
     }
    return handleError<BetaApplication[]> ( -102 , 'Unknown error' );
   }
};


export type BetaApplicationDecision = 'accept' | 'refuse' | 'waitlist';

export interface BetaApplicationDecisionResult {
  id      : number;
  status  : BetaApplicationStatus;
  adminId?: number;
}

export interface BackendResponse_BetaApplicationDecision extends BackendResponse<BetaApplicationDecisionResult> {}

// Accepter crée le compte créateur (et envoie son mail de bienvenue) ; refuser et
// mettre en liste d'attente ne touchent qu'à la candidature. En cas de refus de l'API
// (candidature déjà traitée, e-mail devenu pris...), `comment` porte son motif.
/** Émis après une décision sur une candidature (acceptée, refusée, liste d'attente). */
export const BETA_APPLICATIONS_CHANGED_EVENT = 'beta-applications-changed';

const decideBetaApplication = async ( decision: BetaApplicationDecision ,
                                      id      : number                  ,
                                      adminId?: number                  ): Promise<BackendResponse_BetaApplicationDecision> => {

  const urls: Record<BetaApplicationDecision, string> = {
    accept   : API_URLS.acceptBetaApplication   ,
    refuse   : API_URLS.refuseBetaApplication   ,
    waitlist : API_URLS.waitlistBetaApplication ,
  };

  const url = urls[decision].replace ( '{id}' , `${id}` );

  try
   {
    const response = await axios.post ( url , {} , { headers: { ...buildHeaders ( adminId ) , 'Content-Type': 'application/ld+json' } } );

    if ( response.status === 200 )
          {
           // Le bureau (pastille rouge de l'onglet Bêta-testeur) se remet à jour sans attendre son relevé périodique.
           window.dispatchEvent ( new Event ( BETA_APPLICATIONS_CHANGED_EVENT ) );

           return {
                    code    : 1 ,
                    comment : '' ,
                    payload : response.data ,
                  };
          }
     else {
           return handleError ( response.status , 'Non-200 response from API' );
          }
   }
  catch ( error )
   {
    if ( axios.isAxiosError ( error ) )
     {
      return handleError ( error.response?.status === 409 ? -103 : -101 , error.response?.data?.detail ?? error.response?.data?.message ?? error.message );
     }
    return handleError ( -102 , 'Unknown error' );
   }
};

export const acceptBetaApplication   = ( id: number , adminId?: number ) => decideBetaApplication ( 'accept'   , id , adminId );
export const refuseBetaApplication   = ( id: number , adminId?: number ) => decideBetaApplication ( 'refuse'   , id , adminId );
export const waitlistBetaApplication = ( id: number , adminId?: number ) => decideBetaApplication ( 'waitlist' , id , adminId );
